import { Component, ViewChild, ElementRef, OnDestroy, inject, OnInit } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ActivatedRoute, Router } from '@angular/router';
import {
  LOCATION_ANSWERED,
  OnboardingLocationSheetComponent,
} from '@components/onboarding-location-sheet/onboarding-location-sheet.component';
import { LocationExampleConcern } from '@components/onboarding-location-sheet/location-examples.constants';
import { firstValueFrom } from 'rxjs';
import { ConnectionService } from '@services/connection-service';
import { LocalStorageService } from '@services/local-storage-service';
import { EventLoggerService } from '@services/event-logger-service';
import { BroadcastService } from '@services/broadcast-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { MAX_SECONDARY_CONCERNS_LENGTH, MIN_SECONDARY_CONCERNS_LENGTH } from '@shared/constants';
import { AppConfig } from '../app.config';
import { CurrentComponentService } from '@services/current-component';
import { UserService } from '@services/user';
import { ExperimentService } from '@services/experiment/experiment.service';
import { OnboardingLocationService } from '@services/onboarding/onboarding-location.service';
import { ConsultationSessionService } from '@services/consultation-session-service';

// Concerns with no explicit rank sort to the end of the list.
const UNRANKED_CONCERN_RANK = Number.MAX_SAFE_INTEGER;

@Component({
  selector: 'main-concern',
  templateUrl: './main-concern.html',
  standalone: false,
})
export class MainConcernComponent implements OnDestroy, OnInit {
  public readonly conn: ConnectionService = inject(ConnectionService);
  protected readonly router: Router = inject(Router);
  private readonly localStorageService: LocalStorageService = inject(LocalStorageService);
  public readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  public readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  public readonly appConfig: AppConfig = inject(AppConfig);
  private readonly appWebBridge: AppWebBridgeService = inject(AppWebBridgeService);
  private readonly currentComponent: CurrentComponentService = inject(CurrentComponentService);
  private readonly userService: UserService = inject(UserService);
  private readonly experimentService: ExperimentService = inject(ExperimentService);
  private readonly bottomSheet: MatBottomSheet = inject(MatBottomSheet);
  private readonly locationService: OnboardingLocationService = inject(OnboardingLocationService);
  private readonly consultationSessionService: ConsultationSessionService = inject(ConsultationSessionService);

  @ViewChild('container', { static: false }) container: ElementRef;
  ui: { [key: string]: boolean } = { loading: true, saveInProcess: false };
  private persistedSelectionSignature: string = '';
  serverConcernList: Array<any> = [];
  searchEnabled: boolean = false;
  mainConcerns: Array<any> = [];
  mainConcernsCopy: Array<any> = [];
  classBasedConcerns: any = { FACE: [], BODY: [], HAIR: [] };
  concernSections: any = { CARE: [], TREATMENT: [] };
  searchInput: any;
  searchHash: any = {};
  concernClass: any;
  selectedConcerns: Array<any> = [];
  selectedConcernsDetails: Array<any> = [];
  latestConcernDescription: any;
  isCureSelected: boolean = false;
  newConcernNames: any = {};
  startTimer: number;
  showSecondaryConcerns: boolean = false;
  selectedSecondaryConcerns: any = {};
  isConcernClassHair: boolean = false;
  faceMultiConcernUI: boolean = false;
  hairMultiConcernUI: boolean = false;

  /** Swaps the header chrome for the `user_profile_input_breakdown` design. */
  protected isProfileBreakdownEnabled: boolean = false;

  async ngOnInit(): Promise<any> {
    this.startTimer = new Date().getTime();
    this.ui = { loading: true };
    await this.experimentService.experimentsLoaded;
    this.isProfileBreakdownEnabled = this.experimentService.isUserProfileInputBreakdownEnabled();
    await this.conn.updateLocalUser();
    this.concernClass = this.activatedRoute.snapshot.queryParams.class || this.appConfig.Shared.Regimen.Class.FACE;
    this.isConcernClassHair = this.activatedRoute.snapshot.queryParams.class === this.appConfig.Shared.Regimen.Class.HAIR;

    this.currentComponent.set(this);

    if (this.concernClass === this.appConfig.Shared.Regimen.Class.FACE) {
      this.faceMultiConcernUI = true;
      this.eventLogger.cleverTapEvent('pageOpen',
        JSON.stringify({ pageName: 'Onboarding problem type selection screen - Primary concern' }), [
          this.appConfig.Shared.EventTracker.CLEVER_TAP,
          this.appConfig.Shared.EventTracker.MOENGAGE,
          this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
        ]);
    } else if (this.concernClass === this.appConfig.Shared.Regimen.Class.HAIR) {
      this.hairMultiConcernUI = true;
    }

    await this.fetchConcerns();
    const time = new Date().getTime() - this.startTimer;
    this.eventLogger.trackEvent('concern_selection_page_loading_time', { timeInMillSec: time });
  }

  async fetchConcerns(): Promise<any> {
    this.ui.loading = true;
    if (!this.serverConcernList.length) {
      this.serverConcernList = await this.conn.findMainWithSecondaryConcerns({
        concernClass: this.concernClass,
        shouldTranslateConcernsList: true,
      });
    }
    this.ui.loading = false;
    this.mainConcerns = this.findTopMainConcerns();

    if (this.mainConcerns.length === 0) {
      this.eventLogger.trackInElasticSearch({
        event: 'MAIN_CONCERN_NOT_FOUND',
        type: 'ERROR_MAIN_CONCERN_NOT_FOUND',
        user: this.conn.getActingUser().get('username'),
      });
    }

    this.mainConcernsCopy = this.mainConcerns;
    this.onSelectionChange();
  }

  findTopMainConcerns(): Array<any> {
    const followApiOrder = this.experimentService.skinMainConcernChangesEnabled
      && this.concernClass === this.appConfig.Shared.Regimen.Class.FACE;
    const selectedMainConcerns = followApiOrder
      ? this.serverConcernList
      : [...this.serverConcernList].sort((concern1: { rank?: number }, concern2: { rank?: number }): number => {
        const rankOfConcern1 = concern1.rank ?? UNRANKED_CONCERN_RANK;
        const rankOfConcern2 = concern2.rank ?? UNRANKED_CONCERN_RANK;
        return rankOfConcern1 - rankOfConcern2;
      });

    const finalResult: Array<any> = [];

    if (this.faceMultiConcernUI || this.hairMultiConcernUI) {
      selectedMainConcerns.forEach((concern: any): void => {
        finalResult.push({
          value: concern.value,
          key: concern.displayName,
          keyInEnglish: concern.keyInEnglish || '',
          class: concern.class,
          rank: concern.rank,
          description: concern.description || '',
          section: concern.section,
          concernsList: concern?.concernsList || [],
        });
      });
    } else {
      selectedMainConcerns.forEach((concern: any): void => {
        concern.keywords.forEach((item: any): void => {
          finalResult.push({
            value: concern.value,
            key: item.key,
            keyInEnglish: item.keyInEnglish || '',
            class: concern.class,
            rank: concern.rank,
            description: item.description || concern.description || '',
            section: concern.section,
          });
        });
      });
    }

    return finalResult;
  }

  addRemoveProblem(problem: any): void {
    // check if problem already selected
    const isProblemAlreadySelected = this.selectedConcernsDetails.some(
      (concern: any): boolean => concern.key === problem.key && concern.value === problem.value,
    );
    if (isProblemAlreadySelected) {
      // remove the problem if it's already selected
      this.selectedConcerns = this.selectedConcerns.filter((key: string): boolean => key !== problem.key);
      this.selectedConcernsDetails = this.selectedConcernsDetails.filter(
        (concern: any): boolean => concern.key !== problem.key,
      );
      this.latestConcernDescription = this.selectedConcernsDetails.length > 0
        ? this.selectedConcernsDetails[this.selectedConcernsDetails.length - 1].description
        : undefined;
    } else {
      // replace existing values with the new problem
      this.selectedConcerns = [problem.key];
      this.selectedConcernsDetails = [problem];
      this.latestConcernDescription = problem.description;
    }
  }

  onSelectionChange(): void {
    const searchKey = this.searchInput || '';
    if (!searchKey) {
      this.selectedConcerns = [];
      this.selectedConcernsDetails = [];
      delete this.latestConcernDescription;
    }
    this.mainConcernsCopy = this.mainConcerns.filter((each: any): void => (
      each.key.toLowerCase().includes(searchKey.toLowerCase())
      || each.value.toLowerCase().includes(searchKey.toLowerCase())
      || each.keyInEnglish.toLowerCase().includes(searchKey.toLowerCase())
      || each.value.toLowerCase().includes('something')
      || each.keyInEnglish.toLowerCase().includes('something')));
    this.logSearch();
    this.classBasedConcerns[this.concernClass] = this.mainConcernsCopy.filter((each: any): boolean => each.class === this.concernClass);
    this.concernSections.CARE = this.classBasedConcerns[this.concernClass].filter((each: any): boolean => each.section === 'CARE');
    this.concernSections.TREATMENT = this.classBasedConcerns[this.concernClass]
      .filter((each: any): boolean => each.section === 'TREATMENT');
    if (!searchKey.length && !this.searchEnabled) {
      this.concernSections.CARE = this.concernSections.CARE.slice(0, 8);
      this.concernSections.TREATMENT = this.concernSections.TREATMENT.slice(0, 8);
    }
  }

  async onMainConcernSave(): Promise<any> {
    this.ui.saveInProcess = true;
    const concerns = Array.from(this.selectedConcernsDetails)
      .sort((value1: any, value2: any): number => value1.rank - value2.rank)
      .map((each: any): void => each.value);
    const userSelectedConcern = Array.from(this.selectedConcernsDetails)
      .sort((value1: any, value2: any): number => value1.rank - value2.rank)
      .map((each: any): void => each.key);
    const secondaryConcernsArray = Array.from(Object.values(this.selectedSecondaryConcerns).reduce(
      (acc: Set<any>, curVal: any): Set<string> => {
        acc.add(curVal.value);
        return acc;
      }, new Set()) as Set<any>);
    const selectionSignature = JSON.stringify({ concerns, secondaryConcernsArray });
    const saveConcerns = (): Promise<boolean> => this.saveConcernsToServer(concerns, userSelectedConcern,
      secondaryConcernsArray, selectionSignature);
    const resetSaveState = (): void => { this.ui = { loading: false, saveInProcess: false }; };

    await this.experimentService.experimentsLoaded;
    const isUserProfileInputBreakdownEnabled = this.experimentService.isUserProfileInputBreakdownEnabled();
    if (!isUserProfileInputBreakdownEnabled && !await saveConcerns()) {
      resetSaveState();
      return;
    }
    if (!await this.validateAndAskForLocation()) {
      resetSaveState();
      return;
    }
    if (isUserProfileInputBreakdownEnabled && !await saveConcerns()) {
      resetSaveState();
      return;
    }

    await this.handOffAfterConcernSave(concerns);
    this.ui = { loading: false, saveInProcess: false };
  }

  private async handOffAfterConcernSave(concerns: Array<any>): Promise<void> {
    const { instantCheckupId, redirectOnBack } = this.activatedRoute.snapshot.queryParams;
    if (instantCheckupId) {
      await this.router.navigate([`/user/instantCheckup/${instantCheckupId}`]);
      return;
    }
    const [session]: Array<any> = await this.conn.fetchConsultationSessions({
      where: { user: this.conn.getActingUser(), PrivateMainConcern: concerns[0], archive: false },
    });
    const queryParams: Record<string, string> = { type: 'consultationSession', username: this.conn.getActingUser().get('username') };
    if (redirectOnBack) queryParams.redirectOnBack = redirectOnBack;
    this.appWebBridge.logEventInBranchAndFirebaseFromiOS({
      branch: { name: 'consultationSessionStarted' },
      firebase: { name: 'consultationSessionStarted' },
    });
    await this.navigateToTrustInfoOrChat(concerns, session, queryParams);
  }

  private async navigateToTrustInfoOrChat(
    concerns: Array<any>,
    session: any,
    queryParams: Record<string, string>,
  ): Promise<void> {
    // When the onboarding-trust-info experiment is enabled for this concern class, show the
    // trust-info interstitial (which continues to the chat on Proceed); otherwise keep the
    // legacy chat navigation. Skip the interstitial for the Regular Hair Care concern and go
    // straight to the chat.
    await this.experimentService.experimentsLoaded;
    const isRegularHairCare = concerns[0] === this.appConfig.Shared.String.REGULAR_HAIR_CARE;
    if (!isRegularHairCare && this.experimentService.isOnboardingTrustInfoEnabledForClass(this.concernClass)) {
      const trustInfoQueryParams: Record<string, string> = {
        sessionId: session?.id,
        username: queryParams.username,
        class: this.concernClass,
      };
      if (queryParams.redirectOnBack) trustInfoQueryParams.redirectOnBack = queryParams.redirectOnBack;
      await this.router.navigate(['/trust-info'], { queryParams: trustInfoQueryParams });
      return;
    }
    // No trust interstitial on this path (e.g. Regular Hair Care), so clear the onboarding
    // flag here since trust-info proceed() won't run to clear it.
    this.localStorageService.delete('onboardingFlow');
    await this.router.navigate([`/chatV2/${session?.id}`], { queryParams });
  }

  private async saveConcernsToServer(
    concerns: Array<any>,
    userSelectedConcern: Array<any>,
    secondaryConcernsArray: Array<any>,
    selectionSignature: string,
  ): Promise<boolean> {
    if (selectionSignature === this.persistedSelectionSignature) return true;
    this.eventLogger.cleverTapEvent('concernSelected', JSON.stringify({ name: userSelectedConcern }));
    this.eventLogger.trackEvent('main_concern_selected', { concerns, userSelectedConcern });
    this.conn.updateExperimentLogsInUser({ main_concern_selected: true });
    try {
      await this.restartTreeForNewSelection();
      await this.conn.saveUserMainConcern(concerns);
      this.localStorageService.delete('Onboarding/PrivateMainConcernClass');
      this.localStorageService.delete('Onboarding/locationPageSeen');
      this.appWebBridge.logEventInBranchAndFirebaseFromiOS({
        branch: { name: 'mainConcernUpdated' },
        firebase: { name: 'mainConcernUpdated' },
      });

      if (this.isSecondaryConditionSelected) {
        await this.conn.saveUserSecondaryConcerns(secondaryConcernsArray);
        const consultationSession = await this.conn.findConsultationSession(this.concernClass);
        await consultationSession.set('concernList', secondaryConcernsArray);
        await consultationSession.save();
      }
      this.persistedSelectionSignature = selectionSignature;
      return true;
    } catch (err) {
      this.broadcast.broadcast('NOTIFY', { message: err.toString() });
      return false;
    }
  }

  /**
   * The consultation session and its tree are a server-side effect of writing PrivateMainConcern,
   * and the server keys the session to the concern that started it — so a live session has to be
   * archived before a new concern can start a fresh one. That restart belongs here, at the moment
   * the user commits a selection: resetting any earlier (on back out of trust-info, on "start
   * again") destroys the tree of anyone who then walks away without picking, and an empty
   * PrivateMainConcern reads as incomplete onboarding, which onboardingGuard bounces out of every
   * guarded route until they finish concern selection again.
   *
   * Throws are left to the caller's catch — a failed restart must not fall through to the save, or
   * the concern is written against a session the server will not replace.
   */
  private async restartTreeForNewSelection(): Promise<void> {
    if (!this.conn.getActingUser()?.get('PrivateMainConcern')) return;
    await this.conn.disablePreviousConsultationSessions(this.concernClass);
    this.consultationSessionService.clearLastBotQuestionId(this.concernClass);
  }

  /**
   * The primary concern the sheet picks its example copy from. The concerns are not saved yet at
   * this point in the flow, so the selection has to travel with the sheet rather than be re-read.
   */
  private locationExampleConcern(): LocationExampleConcern {
    const [primaryConcern] = Array.from(this.selectedConcernsDetails ?? [])
      .sort((concern1: any, concern2: any): number => concern1.rank - concern2.rank);
    return { concernClass: this.concernClass, concernValue: primaryConcern?.value };
  }

  /**
   * Shows the location bottomsheet when the split onboarding profile flow is active and the user has
   * not already answered the location step. Resolves false only when they cancelled the sheet
   * (backdrop tap or Android back), so the caller leaves them on the concern screen.
   */
  private async validateAndAskForLocation(): Promise<boolean> {
    await this.experimentService.experimentsLoaded;
    if (!this.experimentService.isUserProfileInputBreakdownEnabled()) return true;
    if (this.conn.getActingUser()?.get('lastLocation')) return true;
    if (this.locationService.hasAskedLocation()) return true;

    this.locationService.markLocationAsked();
    const sheetRef = this.bottomSheet.open(OnboardingLocationSheetComponent, {
      panelClass: 'onboarding-location-sheet-panel',
      backdropClass: 'onboarding-location-sheet-backdrop',
      data: this.locationExampleConcern(),
    });
    this.currentComponent.set(sheetRef.instance);
    try {
      const result = await firstValueFrom(sheetRef.afterDismissed());
      if (result !== LOCATION_ANSWERED) return false;
      this.ui.loading = true;
      return true;
    } finally {
      this.currentComponent.remove(sheetRef.instance);
      this.currentComponent.set(this);
    }
  }

  enableSearch(): void {
    this.container.nativeElement.scrollTop = 0;
    this.searchEnabled = true;
    this.onSelectionChange();
  }

  logSearch(): any {
    if (!this.mainConcernsCopy.length && this.searchInput && !this.searchHash[this.searchInput]) {
      this.searchHash[this.searchInput] = 1;
      this.eventLogger.trackInElasticSearch({
        event: 'MAIN_CONCERN_SEARCH',
        type: 'APP_SEARCH_LOG',
        key: this.searchInput,
        user: this.conn.getActingUser().get('username'),
      });
    }
  }

  onNewFlowClick(flow: any): void {
    this.isCureSelected = flow === 'cure';
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: flow }));
    const problem = {
      class: 'FACE',
      description: 'Get a skin improvement kit made just for you',
      key: 'Better, Healthier skin',
      rank: 1,
      section: 'CARE',
      value: 'NO_CONCERN',
    };
    this.selectedConcerns.push(problem.key);
    this.selectedConcernsDetails.push(problem);
    this.latestConcernDescription = problem.description;
    this.onMainConcernSave();
  }

  openTab(url: string): void {
    this.conn.navigateToURL(url);
  }

  back(): void {
    const redirectOnBack = this.activatedRoute.snapshot.queryParams.redirectOnBack;

    if (this.localStorageService.getBooleanValue('onboardingFlow')) {
      // First onboarding (trust-info experiment): back always returns to class selection,
      // even though the onboarding flow sets redirectOnBack=/home on entry. The flag is set on
      // the class-selection page and cleared once the user proceeds past concern selection.
      this.router.navigate(['/onboarding/concern'], { queryParams: { redirectOnBack: '/home' } });
    } else if (redirectOnBack && redirectOnBack !== '/home') {
      const decodedUrl = decodeURIComponent(redirectOnBack);
      this.router.navigateByUrl(decodedUrl);
    } else {
      this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
    }

    this.eventLogger.cleverTapEvent('clickedBack', JSON.stringify({ pageName: 'concern-selection' }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
  }

  /**
   * Handle device/app back (Android/iOS NAVIGATION_BACK)
   * Behaves same as tapping the header back button.
   * When in secondary concerns screen, go back to primary concerns screen instead of navigating to previous route.
   */
  async handleBackPress(): Promise<boolean> {
    if (this.showSecondaryConcerns) {
      this.handleBackToPrimaryConcerns();
      return true;
    }
    this.back();
    return true;
  }

  handleAddPrimaryConcern(problem: any): void {
    if (!this.ui.saveInProcess) {
      this.addRemoveProblem(problem);
    }
  }

  toggleShowSecondaryConcerns(): void {
    this.showSecondaryConcerns = !this.showSecondaryConcerns;
  }

  async handleShowSecondaryConcernsScreen(): Promise<void> {
    if (this.ui.saveInProcess) return;
    if (this.selectedConcernsDetails?.[0]?.concernsList?.length) {
      this.toggleShowSecondaryConcerns();
      this.eventLogger.cleverTapEvent('pageOpen',
        JSON.stringify({ pageName: 'Onboarding problem type selection screen - Other concerns' }));
      return;
    }
    await this.onMainConcernSave();
  }

  async handleSkipPrimaryConcern(): Promise<void> {
    if (this.ui.saveInProcess) return;
    if (!this.isNoConcernSelected) {
      this.addRemoveProblem(this.concernSections.CARE[0]);
    }
    await this.onMainConcernSave();
  }

  /** Guarded entry point for the header back buttons, so the template carries no composite check. */
  handleBackClick(): void {
    if (this.ui.saveInProcess) return;
    this.back();
  }

  handleBackToPrimaryConcerns(): void {
    if (!this.ui.saveInProcess) {
      this.selectedSecondaryConcerns = {};
      this.toggleShowSecondaryConcerns();
    }
  }

  addRemoveSecondaryConcern(concern: any): void {
    if (this.ui.saveInProcess) return;
    if (this.selectedSecondaryConcerns[concern.displayName]) {
      delete this.selectedSecondaryConcerns[concern.displayName];
    } else if (Object.keys(this.selectedSecondaryConcerns).length < 3) {
      this.selectedSecondaryConcerns[concern.displayName] = concern;
    }
  }

  /** Spacing + entry animation for the secondary-concerns heading, per profile-breakdown variant. */
  get secondaryConcernsHeadingClass(): string {
    return this.isProfileBreakdownEnabled
      ? 'tw-mb-6 tw-animate-onboarding_page_enter'
      : 'tw-mt-6 tw-mb-8';
  }

  /** Entry animation applied to the scrollable concern lists, only in the profile-breakdown variant. */
  get pageEnterAnimationClass(): string {
    return this.isProfileBreakdownEnabled ? 'tw-animate-onboarding_page_enter' : '';
  }

  get isSecondaryConditionSelected(): boolean {
    return Object.keys(this.selectedSecondaryConcerns).length >= MIN_SECONDARY_CONCERNS_LENGTH;
  }

  get isOpacityConditionMet(): boolean {
    return Object.keys(this.selectedSecondaryConcerns).length >= MAX_SECONDARY_CONCERNS_LENGTH;
  }

  /**
   * Whether the currently selected primary concern is a "no concern" / skip option.
   * FACE treats only `NO_CONCERN` as skip; HAIR additionally treats `REGULAR_HAIR_CARE` as skip.
   * Used to decide which primary CTA (Next vs "I don't have any concern") shows the loader.
   */
  get isNoConcernSelected(): boolean {
    const { NO_CONCERN, REGULAR_HAIR_CARE }: Record<string, string> = this.appConfig.Shared.String;
    const selectedValue = this.selectedConcernsDetails?.[0]?.value;
    const noConcernValues = this.hairMultiConcernUI
      ? [NO_CONCERN, REGULAR_HAIR_CARE]
      : [NO_CONCERN];
    return noConcernValues.includes(selectedValue);
  }

  async handleContinueSecondaryConcern(): Promise<void> {
    await this.onMainConcernSave();
  }

  async handleSkipSecondaryConcern(): Promise<void> {
    this.selectedSecondaryConcerns = {};
    await this.onMainConcernSave();
  }

  ngOnDestroy(): void {
    this.currentComponent.remove(this);
  }
}
